import { defineStore } from "pinia";
import { getStart } from "@/api/getStartApi";
import { useSavedStore } from "./savedStore.js";

// Константи
const STORAGE_KEYS = {
  PRODUCTS: "products",
  TPAYID: "tpayid",
  MENU_ID: "menuId",
  TRADEPOINT_ID: "tradepointId",
  ORDER_ID: "orderId",
};

const DEV_MODE_COMPANIES = [
  "cf0c5da0-bc15-4406-b160-2101c57d2044",
  "cc04df52-52be-4656-b471-69b5ac5fd4ea",
];

const SPECIAL_UNITS = ["100g", "100ml"];

// Утиліти для роботи з localStorage
const StorageUtils = {
  getItem(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (e) {
      console.error(`Error reading ${key} from localStorage:`, e);
      return defaultValue;
    }
  },

  setItem(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      console.error(`Error writing ${key} to localStorage:`, e);
      return false;
    }
  },

  removeItem(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.error(`Error removing ${key} from localStorage:`, e);
      return false;
    }
  },

  getString(key, defaultValue = null) {
    try {
      return localStorage.getItem(key) || defaultValue;
    } catch (e) {
      console.error(`Error reading ${key} from localStorage:`, e);
      return defaultValue;
    }
  },

  setString(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.error(`Error writing ${key} to localStorage:`, e);
      return false;
    }
  },
};

export const useStore = defineStore("startSettings", {
  state: () => ({
    // Режим розробки
    devMode: false,

    // Стан завантаження
    loading: false,

    // Основні налаштування
    startSettings: {},

    // Кошик
    basketDishes: [],

    // ID замовлення
    orderId: StorageUtils.getString(STORAGE_KEYS.ORDER_ID),

    // Ідентифікатори
    tpayid: StorageUtils.getString(STORAGE_KEYS.TPAYID),
    menuId: StorageUtils.getString(STORAGE_KEYS.MENU_ID),
    tradepointId: StorageUtils.getString(STORAGE_KEYS.TRADEPOINT_ID),

    // WebSocket
    socketMessage: null,
    socketError: null,

    // Кеш для оптимізації
    _productsMap: null,
    _categoriesMap: null,
  }),

  getters: {
    // Перевірка чи є налаштування
    hasSettings: (state) => {
      return state.startSettings && Object.keys(state.startSettings).length > 0;
    },

    // Отримання продуктів як Map для швидкого доступу
    productsMap: (state) => {
      if (!state._productsMap && state.startSettings?.products) {
        state._productsMap = new Map(
          state.startSettings.products.map((p) => [p.nomenclatureId, p])
        );
      }
      return state._productsMap || new Map();
    },

    // Отримання категорій як Map
    categoriesMap: (state) => {
      if (!state._categoriesMap && state.startSettings?.categoryTree) {
        state._categoriesMap = new Map(
          Object.entries(state.startSettings.categoryTree)
        );
      }
      return state._categoriesMap || new Map();
    },

    // Загальна кількість товарів в кошику
    basketCount: (state) => {
      return state.basketDishes.reduce((sum, item) => sum + item.count, 0);
    },

    // Загальна сума кошика
    basketTotal: (state) => {
      return state.basketDishes.reduce((sum, item) => {
        const product = state.productsMap.get(item.nomenclatureId);
        return sum + (product ? product.price * item.count : 0);
      }, 0);
    },
  },

  actions: {
    // Завантаження товарів з кошика
    loadBasketDishes() {
      const products = this.startSettings.products || [];
      const saved = StorageUtils.getItem(STORAGE_KEYS.PRODUCTS, []);

      if (!Array.isArray(saved)) {
        this.basketDishes = [];
        StorageUtils.setItem(STORAGE_KEYS.PRODUCTS, []);
        return;
      }

      // Створюємо Set для швидкого пошуку
      const validIds = new Set(products.map((p) => p.nomenclatureId));

      // Фільтруємо недійсні товари
      const validBasket = saved.filter((item) =>
        validIds.has(item.nomenclatureId)
      );

      this.basketDishes = validBasket;

      // Оновлюємо localStorage якщо були видалені недійсні товари
      if (validBasket.length !== saved.length) {
        StorageUtils.setItem(STORAGE_KEYS.PRODUCTS, validBasket);
      }
    },

    // Основна ініціалізація магазину
    async loadStartSettings() {
      if (!this.tpayid && !this.menuId) {
        console.warn("Відсутній tpayid або menuId.");
        return false;
      }

      // Запобігаємо паралельним завантаженням
      if (this.loading) {
        console.warn("Завантаження вже в процесі");
        return false;
      }

      this.loading = true;

      try {
        // Формуємо параметри запиту
        const requestParams = {
          tpayid: this.menuId ? null : this.tpayid,
          menuId: this.menuId || null,
          tradepointId: this.tradepointId || null,
        };

        const data = await getStart(requestParams);

        if (!data) {
          throw new Error("Порожня відповідь від сервера");
        }

        // Обробляємо отримані дані
        await this.processStartSettings(data);

        // Встановлюємо dev mode якщо потрібно
        this.checkDevMode();

        return true;
      } catch (error) {
        console.error("Помилка завантаження налаштувань:", error);
        this.startSettings = {};
        return false;
      } finally {
        this.loading = false;
      }
    },

    // Обробка налаштувань
    async processStartSettings(data) {
      // Очищаємо кеш
      this._productsMap = null;
      this._categoriesMap = null;

      this.startSettings = data;

      // Обробка цін для спеціальних одиниць
      if (this.startSettings?.products) {
        this.processProductPrices();
        // Після перерахунку цін, бо для SPECIAL_UNITS ціна береться з price_out
        this.removeZeroPricedProducts();
      }

      // Фільтрація категорій та продуктів
      if (this.startSettings?.categoryTree && this.startSettings?.products) {
        this.filterCategoriesAndProducts();
      }

      // Створюємо впорядкований масив категорій
      this.createOrderedCategoryTree();

      // Завантажуємо збережені товари
      const savedStore = useSavedStore();
      savedStore.loadSaved();

      // Відновлюємо кошик
      this.loadBasketDishes();
      this.restoreBasketFromStorage();
    },

    // Обробка цін продуктів
    processProductPrices() {
      this.startSettings.products = this.startSettings.products.map(
        (product) => {
          if (SPECIAL_UNITS.includes(product.nomenclatureUnit)) {
            return {
              ...product,
              price: product.price_out * 100,
            };
          }
          return product;
        }
      );
    },

    // Вимога LiqPay до сайту мерчанта, п.2: кожна позиція має містити
    // «актуальну ціну (ціна не може бути нульовою)». Позиції без ціни ховаємо
    // з меню — інакше перевірка мерчанта не проходить. У касі вони лишаються,
    // це лише фільтр відображення.
    removeZeroPricedProducts() {
      const priced = this.startSettings.products.filter(
        (product) => Number(product.price) > 0
      );
      if (priced.length !== this.startSettings.products.length) {
        this.startSettings.products = priced;
      }
    },

    // Фільтрація категорій та продуктів
    filterCategoriesAndProducts() {
      const productsMap = new Map(
        this.startSettings.products.map((p) => [p.nomenclatureId, p])
      );

      const filteredCategories = {};

      for (const [categoryId, category] of Object.entries(
        this.startSettings.categoryTree
      )) {
        // Фільтруємо продукти в категорії
        const validProducts = category.products
          .filter((productId) => productsMap.has(productId))
          .map((productId) => productsMap.get(productId));

        // Фільтруємо підкатегорії
        const validChildren = {};
        if (category.children) {
          for (const [childId, child] of Object.entries(category.children)) {
            const validChildProducts = (child.products || [])
              .filter((productId) => productsMap.has(productId))
              .map((productId) => productsMap.get(productId));

            if (validChildProducts.length > 0) {
              validChildren[childId] = {
                ...child,
                products: validChildProducts,
              };
            }
          }
        }

        // Зберігаємо категорію якщо вона має продукти або підкатегорії
        if (validProducts.length > 0 || Object.keys(validChildren).length > 0) {
          filteredCategories[categoryId] = {
            ...category,
            products: validProducts,
            children: validChildren,
          };
        }
      }

      this.startSettings.categoryTree = filteredCategories;
    },

    // Створення впорядкованого дерева категорій
    createOrderedCategoryTree() {
      if (this.startSettings?.category && this.startSettings?.categoryTree) {
        this.startSettings.orderedCategoryTree = this.startSettings.category
          .map((cat) => this.startSettings.categoryTree[cat.categoryId])
          .filter(Boolean);
      }
    },

    // Відновлення кошика з localStorage
    restoreBasketFromStorage() {
      if (this.basketDishes.length > 0 && this.startSettings?.products) {
        this.basketDishes.forEach((basketItem) => {
          const product = this.productsMap.get(basketItem.nomenclatureId);
          if (product) {
            product.count = basketItem.count;
          }
        });
      }
    },

    // Перевірка dev mode
    checkDevMode() {
      this.devMode = !!(
        this.startSettings?.companyId &&
        DEV_MODE_COMPANIES.includes(this.startSettings.companyId)
      );
    },

    // Встановлення ідентифікаторів
    setTpayid(id) {
      this.tpayid = id;
      StorageUtils.setString(STORAGE_KEYS.TPAYID, id);
    },

    setMenuId(id) {
      // Виправлено назву методу
      this.menuId = id;
      StorageUtils.setString(STORAGE_KEYS.MENU_ID, id);
    },

    setTradepointId(id) {
      // Виправлено назву методу
      this.tradepointId = id;
      StorageUtils.setString(STORAGE_KEYS.TRADEPOINT_ID, id);
    },

    // Методи для сумісності зі старим кодом
    setmenuId(id) {
      console.warn("Deprecated: use setMenuId instead of setmenuId");
      this.setMenuId(id);
    },

    setTradepointid(id) {
      console.warn(
        "Deprecated: use setTradepointId instead of setTradepointid"
      );
      this.setTradepointId(id);
    },

    // Робота з кошиком
    addToBasket(product, count = 1) {
      const existingItem = this.basketDishes.find(
        (item) => item.nomenclatureId === product.nomenclatureId
      );

      if (existingItem) {
        existingItem.count += count;
      } else {
        this.basketDishes.push({
          nomenclatureId: product.nomenclatureId,
          count: count,
        });
      }

      StorageUtils.setItem(STORAGE_KEYS.PRODUCTS, this.basketDishes);
    },

    removeFromBasket(productId) {
      const index = this.basketDishes.findIndex(
        (item) => item.nomenclatureId === productId
      );

      if (index !== -1) {
        this.basketDishes.splice(index, 1);
        StorageUtils.setItem(STORAGE_KEYS.PRODUCTS, this.basketDishes);
      }
    },

    updateBasketItemCount(productId, count) {
      const item = this.basketDishes.find(
        (item) => item.nomenclatureId === productId
      );

      if (item) {
        if (count <= 0) {
          this.removeFromBasket(productId);
        } else {
          item.count = count;
          StorageUtils.setItem(STORAGE_KEYS.PRODUCTS, this.basketDishes);
        }
      }
    },

    resetBasket() {
      this.basketDishes = [];
      StorageUtils.setItem(STORAGE_KEYS.PRODUCTS, []);
    },

    // Зміна теми
    changeTheme(data, isSwipe = false) {
      if (!data) return;

      const bodyStyles = document.body.style;

      for (const [key, value] of Object.entries(data)) {
        if (key === "name") continue;

        const cssVariable = this.convertToCssVariable(key);
        bodyStyles.setProperty(cssVariable, value, "important");
      }

      bodyStyles.setProperty("--app-bg", isSwipe ? data.footer : data.content);
    },

    convertToCssVariable(key) {
      const parts = key.split("_");
      return "--" + parts.join("-");
    },

    // Видалення замовлення
    removeOrder() {
      StorageUtils.removeItem(STORAGE_KEYS.ORDER_ID);
      this.orderId = null;
      this.resetBasket();
    },

    // WebSocket методи
    setSocketMessage(msg) {
      this.socketMessage = msg;
    },

    setSocketError(error) {
      this.socketError = error;
    },
  },
});
